<template>
  <div class="container-fluid mt-4">
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
      <div>
        <h2 class="mb-1"><i class="bi bi-calendar2-week me-2"></i>TrainingPeaks</h2>
        <p class="text-muted mb-0">Calendario del entrenador, notas del día y comentarios. Se actualiza cada hora.</p>
      </div>
      <button type="button" class="btn btn-outline-primary" :disabled="sincronizando || !configured" @click="sincronizar">
        <span v-if="sincronizando" class="spinner-border spinner-border-sm me-1"></span>
        Actualizar ahora
      </button>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="lastError" class="alert alert-warning">{{ lastError }}</div>

    <div v-if="!configured && !cargando" class="alert alert-light border">
      Conecta TrainingPeaks en <router-link to="/perfil">Perfil</router-link> con la cookie <code>Production_tpAuth</code>.
    </div>

    <div v-if="avisos.length" class="alert alert-primary d-flex flex-wrap justify-content-between align-items-start gap-2">
      <div>
        <strong>Nuevo del entrenador</strong>
        <ul class="mb-0 mt-2 ps-3">
          <li v-for="aviso in avisos" :key="aviso.id">
            <span v-if="aviso.dia" class="text-muted">{{ formatDia(aviso.dia) }} · </span>{{ aviso.texto }}
          </li>
        </ul>
      </div>
      <button type="button" class="btn btn-sm btn-primary" @click="marcarLeidos">Entendido</button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <div v-else-if="configured">
      <p v-if="lastSyncAt" class="text-muted small">Última sincronización: {{ formatSync(lastSyncAt) }}</p>
      <div class="card border-0 shadow-sm mb-4">
        <div class="card-body">
          <TpProximosWidget :configured="true" :entrenos="proximos" />
        </div>
      </div>
      <div class="form-check mb-3">
        <input id="tp-todo" v-model="verTodo" class="form-check-input" type="checkbox">
        <label class="form-check-label" for="tp-todo">Incluir días anteriores (14 atrás y 42 adelante)</label>
      </div>

      <div v-if="!dias.length" class="text-muted">No hay entrenos ni notas en este rango.</div>

      <div v-for="dia in dias" :key="dia.fecha" class="card border-0 shadow-sm mb-3">
        <div class="card-header bg-white d-flex justify-content-between align-items-center">
          <strong class="text-capitalize">
            {{ formatDia(dia.fecha) }}
            <span v-if="dia.fecha === today" class="badge bg-primary ms-1">Hoy</span>
          </strong>
        </div>
        <div class="card-body">
          <div v-for="nota in dia.notas" :key="nota.id" class="border rounded p-3 mb-3 bg-light">
            <div class="small text-uppercase text-muted fw-semibold mb-1">Nota del día</div>
            <div class="fw-semibold">{{ nota.title || 'Nota' }}</div>
            <div v-if="nota.description" class="mt-1" style="white-space: pre-wrap;">{{ nota.description }}</div>
            <div v-for="(c, i) in comentarios(nota)" :key="c.id || i" class="small mt-2" :class="{ 'tp-comment-mine': c.mine }">
              <i class="bi bi-chat-left-text me-1"></i>
              <strong>{{ etiquetaAutor(c) }}: </strong>{{ c.text }}
            </div>
          </div>

          <article v-for="w in dia.entrenos" :key="w.id" class="tp-workout py-3">
            <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
              <h3 class="h5 mb-0">{{ w.title }}</h3>
              <span v-if="w.completed" class="badge bg-success">Hecho</span>
            </div>
            <div class="tp-metrics">
              <span class="badge bg-light text-dark border">{{ w.sport }}</span>
              <span v-if="metricaVisible(vista(w).clock, '--:--:--')" class="tp-metric">{{ vista(w).clock }}</span>
              <span v-if="metricaVisible(vista(w).distance, '-- km')" class="tp-metric">{{ vista(w).distance }}</span>
              <span v-if="metricaVisible(vista(w).tss, '--')" class="tp-metric">{{ vista(w).tss }} TSS</span>
            </div>
            <div v-if="w.exercises && w.exercises.length" class="small text-muted mt-2">{{ w.exercises.join(' · ') }}</div>
            <div v-if="vista(w).bars.length" class="tp-bar" aria-hidden="true">
              <span
                v-for="(bar, i) in vista(w).bars"
                :key="i"
                class="tp-bar-seg"
                :class="'tp-tone-' + bar.tone"
                :style="{ flexGrow: bar.flex, height: bar.height + '%' }"
              ></span>
            </div>
            <div v-if="w.description" class="tp-note">
              <div class="tp-note-label">Descripción</div>
              <div class="tp-note-body">{{ w.description }}</div>
            </div>
            <div v-if="w.coachComments" class="tp-note tp-note-coach">
              <div class="tp-note-label">Antes del entreno</div>
              <div class="tp-note-body">{{ w.coachComments }}</div>
            </div>
            <div v-if="vista(w).blocks.length" class="tp-details">
              <div class="tp-note-label">Detalle</div>
              <div v-for="(block, bi) in vista(w).blocks" :key="bi" class="tp-block">
                <div v-if="block.repeat && block.reps > 1" class="fw-semibold">Repetir {{ block.reps }} veces</div>
                <div v-for="(step, si) in block.steps" :key="si" class="tp-step">
                  <div class="fw-semibold">
                    <span v-if="block.repeat && block.reps > 1">{{ si + 1 }}. </span>{{ step.name }}
                  </div>
                  <div v-if="step.detail" class="text-muted">{{ step.detail }}</div>
                  <div v-if="step.zone" class="text-muted">{{ step.zone }}</div>
                </div>
              </div>
            </div>
            <div v-if="w.athleteComments" class="tp-note tp-note-athlete">
              <div class="tp-note-label">Mi comentario</div>
              <div class="tp-note-body">{{ w.athleteComments }}</div>
            </div>
            <div v-if="w.privateNote" class="tp-note">
              <div class="tp-note-label">Nota privada</div>
              <div class="tp-note-body">{{ w.privateNote }}</div>
            </div>
            <div v-for="(c, i) in comentarios(w)" :key="c.id || i" class="small mt-2" :class="{ 'tp-comment-mine': c.mine }">
              <i class="bi bi-chat-left-text me-1"></i>
              <strong>{{ etiquetaAutor(c) }}: </strong>{{ c.text }}
            </div>
          </article>
          <p v-if="!dia.entrenos.length && !dia.notas.length" class="text-muted small mb-0">Sin plan.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import TpProximosWidget from '../components/TpProximosWidget.vue';

export default {
  name: 'SaludTrainingPeaks',
  components: { TpProximosWidget },
  data() {
    return {
      cargando: true,
      sincronizando: false,
      configured: false,
      error: '',
      lastError: '',
      lastSyncAt: null,
      today: '',
      workouts: [],
      proximos: [],
      notes: [],
      avisos: [],
      verTodo: false,
    };
  },
  computed: {
    dias() {
      const desde = this.verTodo ? null : this.today;
      const hasta = this.verTodo ? null : this.shift(this.today, 21);
      const map = new Map();
      const entra = (fecha) => {
        if (!fecha) return false;
        if (desde && fecha < desde) return false;
        if (hasta && fecha > hasta) return false;
        return true;
      };
      const asegurar = (fecha) => {
        if (!map.has(fecha)) map.set(fecha, { fecha, entrenos: [], notas: [] });
        return map.get(fecha);
      };
      for (const w of this.workouts) {
        if (!entra(w.date)) continue;
        asegurar(w.date).entrenos.push(w);
      }
      for (const n of this.notes) {
        if (n.isHidden || !entra(n.date)) continue;
        asegurar(n.date).notas.push(n);
      }
      return [...map.values()].sort((a, b) => a.fecha.localeCompare(b.fecha));
    },
  },
  mounted() {
    this.cargar();
  },
  methods: {
    shift(iso, days) {
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      dt.setUTCDate(dt.getUTCDate() + days);
      return dt.toISOString().slice(0, 10);
    },
    formatDia(iso) {
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-ES', {
        weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC',
      });
    },
    formatSync(value) {
      const d = new Date(String(value).includes('T') ? value : `${String(value).replace(' ', 'T')}Z`);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
    },
    comentarios(item) {
      return (item.comments || []).filter((c) => c && c.text);
    },
    etiquetaAutor(comment) {
      if (comment && comment.mine) return 'Tú';
      return (comment && comment.author) || 'Comentario';
    },
    vista(workout) {
      if (workout && workout.view) return workout.view;
      return { clock: '--:--:--', distance: '-- km', tss: '--', bars: [], blocks: [] };
    },
    metricaVisible(value, vacio) {
      const text = String(value || '').trim();
      return Boolean(text && text !== vacio);
    },
    async cargar() {
      this.cargando = true;
      this.error = '';
      try {
        const { data } = await axios.get('/api/trainingpeaks/calendar');
        this.configured = !!data.configured;
        this.lastSyncAt = data.lastSyncAt;
        this.lastError = data.lastError || '';
        this.today = data.today;
        this.workouts = data.workouts || [];
        this.proximos = data.proximos || [];
        this.notes = data.notes || [];
        this.avisos = data.avisos || [];
      } catch (err) {
        this.error = err.response?.data?.error || 'No se pudo cargar el calendario';
      } finally {
        this.cargando = false;
      }
    },
    async sincronizar() {
      this.sincronizando = true;
      this.error = '';
      try {
        await axios.post('/api/trainingpeaks/sync');
        await this.cargar();
      } catch (err) {
        this.error = err.response?.data?.error || 'Error al sincronizar';
      } finally {
        this.sincronizando = false;
      }
    },
    async marcarLeidos() {
      try {
        await axios.post('/api/trainingpeaks/avisos/leer');
        this.avisos = [];
      } catch (err) {
        this.error = err.response?.data?.error || 'No se pudieron marcar los avisos';
      }
    },
  },
};
</script>

<style scoped>
.tp-workout + .tp-workout {
  border-top: 1px solid #e9ecef;
}
.tp-metrics {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.85rem;
  margin-top: 0.35rem;
}
.tp-metric {
  font-variant-numeric: tabular-nums;
  font-size: 1.25rem;
  font-weight: 650;
  letter-spacing: -0.02em;
}
.tp-bar {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 52px;
  margin: 0.8rem 0 0.2rem;
  padding: 0 2px;
  background: #eef3f8;
  border-radius: 6px;
  overflow: hidden;
}
.tp-bar-seg {
  min-width: 3px;
  border-radius: 3px 3px 0 0;
}
.tp-tone-work { background: #1d4f91; }
.tp-tone-warmup { background: #4c86c7; }
.tp-tone-cooldown { background: #8eb4dc; }
.tp-tone-easy { background: #c5d0dc; }
.tp-note {
  margin-top: 0.75rem;
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  background: #f6f8fa;
}
.tp-note-coach {
  background: #fff;
  border: 1px solid #d6e2f0;
}
.tp-note-athlete {
  background: #f3f8f3;
  border: 1px solid #d5e8d5;
}
.tp-comment-mine {
  color: #1f5133;
}
.tp-note-label {
  margin-bottom: 0.2rem;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.tp-note-body {
  white-space: pre-wrap;
}
.tp-details {
  margin-top: 0.9rem;
}
.tp-block + .tp-block {
  margin-top: 0.75rem;
}
.tp-step {
  margin-top: 0.35rem;
}
</style>
